import { Suspense, lazy, type ReactNode, useEffect } from "react";
import { Navigate, useRoutes, useParams, Outlet } from "react-router-dom";

import AppShell from "@/shell/AppShell";
import { InterviewApi } from "@/api";
import { useProjectStore } from "@/state/projectStore";

const Landing = lazy(() => import("@/screens/Landing"));
const YourInfo = lazy(() => import("@/screens/YourInfo"));
const YourRepresentative = lazy(() => import("@/screens/YourRepresentative"));
const Interview = lazy(() => import("@/screens/interview"));
const LetterReview = lazy(() => import("@/screens/letters/LetterReview"));
const ThankYou = lazy(() => import("@/screens/ThankYou"));

function ProjectLoader() {
  const { displayName } = useParams<{ displayName: string }>();
  const { project, loading, error, setProject, setLoading, setError, reset } = useProjectStore();

  useEffect(() => {
    if (!displayName) return;

    if (project && project.display_name === displayName) return;

    let cancelled = false;
    reset();
    setLoading(true);

    InterviewApi.lookupProject(displayName)
      .then((data) => {
        if (!cancelled) setProject(data, displayName);
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Project not found";
          setError(message);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [displayName, project, setProject, setLoading, setError, reset]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-neutral-600">Loading project...</div>;
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-bold text-black">Project Not Found</h1>
        <p className="text-neutral-600">{error}</p>
      </div>
    );
  }

  return <Outlet />;
}

export default function AppRoutes() {
  return useRoutes([
    {
      path: ":displayName",
      element: <AppShell />,
      children: [
        {
          element: <ProjectLoader />,
          children: [
            { index: true, element: withSuspense(<Landing />) },
            { path: "your-info", element: withSuspense(<YourInfo />) },
            { path: "your-rep", element: withSuspense(<YourRepresentative />) },
            { path: "interview/:step", element: withSuspense(<Interview />) },
            { path: "review", element: withSuspense(<LetterReview />) },
            { path: "thank-you", element: withSuspense(<ThankYou />) },
            { path: "*", element: <Navigate to="." replace /> }
          ]
        }
      ]
    },
    {
      path: "*",
      element: (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-2xl font-bold text-black">Welcome</h1>
          <p className="text-neutral-600">Please use a project-specific URL to get started.</p>
        </div>
      )
    }
  ]);
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<div className="p-6 text-neutral-600">Loading...</div>}>{node}</Suspense>;
}

